import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, TrendingUp, Activity, BarChart3, Plus, MoreVertical, Edit2, Share2, Trash2 } from 'lucide-react';
import { DashboardCreationModal } from '../DashboardCreationModal';
interface DashboardsViewProps {
  dashboards: Array<{
    id: string;
    name: string;
    description: string;
    lastModified: string;
    charts: number;
  }>;
  allAvailableDashboards: Array<{
    id: string;
    name: string;
    description: string;
    category: string;
  }>;
  setSelectedDashboardId: (id: string) => void;
  onAddDashboard?: (dashboardId: string) => void;
  onRemoveDashboard?: (dashboardId: string) => void;
  onCreateDashboard?: (prompt: string) => Promise<void>;
  onModalOpenChange?: (isOpen: boolean) => void;
}
export const DashboardsView: React.FC<DashboardsViewProps> = ({
  dashboards,
  allAvailableDashboards,
  setSelectedDashboardId,
  onAddDashboard,
  onRemoveDashboard,
  onCreateDashboard,
  onModalOpenChange
}) => {
  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  // Notify parent when modal state changes
  const handleModalOpenChange = (isOpen: boolean) => {
    setIsCreationModalOpen(isOpen);
    if (onModalOpenChange) {
      onModalOpenChange(isOpen);
    }
  };
  const handleAddDashboard = (dashboardId: string) => {
    if (onAddDashboard) {
      onAddDashboard(dashboardId);
    }
  };
  const handleRemoveDashboard = (dashboardId: string) => {
    if (onRemoveDashboard) {
      onRemoveDashboard(dashboardId);
    }
  };
  const handleCreateDashboard = async (prompt: string) => {
    if (onCreateDashboard) {
      await onCreateDashboard(prompt);
    }
  };
  const handleEdit = (dashboardId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenDropdownId(null);
    console.log('Edit dashboard:', dashboardId);
    // TODO: Implement edit functionality
  };
  const handleShare = (dashboardId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenDropdownId(null);
    console.log('Share dashboard:', dashboardId);
    // TODO: Implement share functionality
  };
  const handleDelete = (dashboardId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenDropdownId(null);
    console.log('Delete dashboard:', dashboardId);
    // TODO: Implement delete functionality
  };
  return <div className="flex flex-col h-full" data-id="element-1377">
      {/* Header with title and actions */}
      <div className="flex items-center justify-between mb-8" data-id="element-1378">
        <div data-id="element-1379">
          <h1 className="text-3xl font-bold text-slate-900 mb-1" data-id="element-1380">Dashboards</h1>
          <p className="text-slate-600 text-sm" data-id="element-1381">
            Manage and organize your custom dashboards
          </p>
        </div>
        <button onClick={() => handleModalOpenChange(true)} className="flex items-center gap-2 px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg font-medium transition-all shadow-sm hover:shadow-md" data-id="element-1382">
          <Plus size={18} data-id="element-1383" />
          New Dashboard
        </button>
      </div>

      {/* Dashboard grid or empty state */}
      {dashboards.length === 0 ? <div className="flex-1 flex items-center justify-center" data-id="element-1384">
          <div className="text-center max-w-md" data-id="element-1385">
            <div className="w-16 h-16 bg-pink-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1386">
              <LayoutDashboard size={32} className="text-pink-500" data-id="element-1387" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2" data-id="element-1388">
              No dashboards yet
            </h3>
            <p className="text-slate-600 text-sm mb-6" data-id="element-1389">
              Create your first dashboard to start organizing your market
              analysis and forecasts
            </p>
            <button onClick={() => handleModalOpenChange(true)} className="flex items-center gap-2 px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg font-medium transition-all shadow-sm mx-auto" data-id="element-1390">
              <Plus size={18} data-id="element-1391" />
              Create Dashboard
            </button>
          </div>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1392">
          {dashboards.map(dashboard => <div key={dashboard.id} onClick={() => setSelectedDashboardId(dashboard.id)} className="bg-white/80 backdrop-blur-sm border border-slate-200 rounded-xl p-6 hover:border-pink-500 hover:shadow-lg transition-all cursor-pointer group relative flex flex-col" data-id="element-1393">
              <div className="flex items-center justify-between mb-4" data-id="element-1394">
                <h3 className="text-lg font-bold text-slate-900 transition-colors pr-8" data-id="element-1395">
                  {dashboard.name}
                </h3>

                {/* Three-dot menu */}
                <div className="absolute top-4 right-4" onClick={e => e.stopPropagation()} data-id="element-1396">
                  <button onClick={e => {
              e.stopPropagation();
              setOpenDropdownId(openDropdownId === dashboard.id ? null : dashboard.id);
            }} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100" data-id="element-1397">
                    <MoreVertical size={18} className="text-slate-400" data-id="element-1398" />
                  </button>

                  {/* Dropdown menu */}
                  <AnimatePresence data-id="element-1399">
                    {openDropdownId === dashboard.id && <>
                        {/* Backdrop to close dropdown */}
                        <div className="fixed inset-0 z-10" onClick={e => {
                  e.stopPropagation();
                  setOpenDropdownId(null);
                }} data-id="element-1400" />

                        <motion.div initial={{
                  opacity: 0,
                  scale: 0.95,
                  y: -10
                }} animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  scale: 0.95,
                  y: -10
                }} transition={{
                  duration: 0.15
                }} className="absolute right-0 top-full mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-xl py-1 z-20" data-id="element-1401">
                          <button onClick={e => handleEdit(dashboard.id, e)} className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors" data-id="element-1402">
                            <Edit2 size={14} data-id="element-1403" />
                            Edit
                          </button>
                          <button onClick={e => handleShare(dashboard.id, e)} className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors" data-id="element-1404">
                            <Share2 size={14} data-id="element-1405" />
                            Share
                          </button>
                          <div className="my-1 border-t border-slate-100" data-id="element-1406" />
                          <button onClick={e => handleDelete(dashboard.id, e)} className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors" data-id="element-1407">
                            <Trash2 size={14} data-id="element-1408" />
                            Delete
                          </button>
                        </motion.div>
                      </>}
                  </AnimatePresence>
                </div>
              </div>

              <p className="text-slate-600 text-sm line-clamp-2 flex-1 mb-4" data-id="element-1409">
                {dashboard.description}
              </p>

              <div className="flex items-center justify-between mt-auto" data-id="element-1410">
                <div className="flex items-center gap-2" data-id="element-1411">
                  <BarChart3 size={16} className="text-slate-400 group-hover:text-pink-500 transition-colors" data-id="element-1412" />
                  <span className="text-xs text-slate-500" data-id="element-1413">
                    {dashboard.lastModified}
                  </span>
                </div>
                <div className="w-8 h-8 rounded-full bg-slate-100 group-hover:bg-pink-500 flex items-center justify-center transition-colors" data-id="element-1414">
                  <LayoutDashboard size={16} className="text-slate-400 group-hover:text-white transition-colors" data-id="element-1415" />
                </div>
              </div>
            </div>)}
        </div>}

      {/* Dashboard Creation Modal */}
      <DashboardCreationModal isOpen={isCreationModalOpen} onClose={() => handleModalOpenChange(false)} allAvailableDashboards={allAvailableDashboards} existingDashboardIds={dashboards.map(d => d.id)} onAddDashboard={handleAddDashboard} onRemoveDashboard={handleRemoveDashboard} onCreateDashboard={handleCreateDashboard} data-id="element-1416" />
    </div>;
};